import React, { useEffect, useState } from "react";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useBooking } from "@/components/BookingContext";
import { CALENDAR_BOOKING_URL, CALENDAR_EMBED_URL } from "@/lib/site";

export function BookingModal() {
    const { isOpen, close } = useBooking();
    // "loading" -> spinner, "ok" -> scheduler, "fail" -> fallback
    const [status, setStatus] = useState("loading");

    useEffect(() => {
        if (!isOpen) {
            setStatus("loading");
            return;
        }
        if (!CALENDAR_EMBED_URL) {
            setStatus("fail");
            return;
        }
        setStatus("loading");
        const t = setTimeout(() => {
            setStatus((s) => (s === "ok" ? s : "fail"));
        }, 8000);
        return () => clearTimeout(t);
    }, [isOpen]);

    return (
        <Dialog open={isOpen} onOpenChange={(v) => (v ? null : close())}>
            <DialogContent
                data-testid="booking-modal"
                className="max-w-2xl w-[calc(100vw-2rem)] p-0 gap-0 overflow-hidden border-border bg-background rounded-2xl"
            >
                <div className="px-6 py-5 border-b border-border pr-14">
                    <DialogTitle className="font-display text-xl font-semibold tracking-tight">
                        Book a Discovery Call<span className="period">.</span>
                    </DialogTitle>
                    <DialogDescription className="text-sm text-muted-foreground mt-1">
                        Free 30-minute call, no pitch.
                    </DialogDescription>
                </div>

                {status === "fail" ? (
                    <div data-testid="booking-fallback" className="bg-surface px-6 py-16 flex flex-col items-center text-center gap-5">
                        <p className="max-w-sm text-muted-foreground leading-relaxed">
                            Trouble loading the scheduler. Book directly instead.
                        </p>
                        <a
                            data-testid="booking-open-newtab"
                            href={CALENDAR_BOOKING_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group inline-flex items-center gap-2.5 rounded-full bg-primary text-primary-foreground px-7 py-3.5 text-base font-medium transition-colors duration-300 hover:bg-foreground/85"
                        >
                            Open scheduling page
                            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                        </a>
                    </div>
                ) : (
                    <div className="relative bg-surface h-[72vh] sm:h-[600px] sm:min-h-[550px]">
                        <iframe
                            data-testid="booking-iframe"
                            title="Schedule a discovery call"
                            src={CALENDAR_EMBED_URL}
                            onLoad={() => setStatus("ok")}
                            onError={() => setStatus("fail")}
                            className="w-full h-full bg-white"
                            frameBorder="0"
                        />
                        {status === "loading" && (
                            <div
                                data-testid="booking-loading"
                                className="absolute inset-0 flex items-center justify-center bg-surface"
                            >
                                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                            </div>
                        )}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
